import { FieldTypes } from '../../constants/FieldTypes'
import type { DropdownField, FieldMap, NumberField, TextField } from '../../types/forms/fields'
import { AnyObject, FieldMapValue } from '../../types/utils'

export type LabResultFlag = 'normal' | 'high' | 'low' | 'abnormal' | 'critical'

export type StandardLabType =
  | 'blood-type-abo-rh'
  | 'cbc'
  | 'rubella'
  | 'hep-b-sag'
  | 'hiv'
  | 'syphilis-rpr'
  | 'gc-chlamydia'
  | 'urine-culture'
  | 'pap'
  | 'gct'
  | 'gtt'
  | 'gbs'
  | 'ua'
  | 'tsh'
  | 'antibody-screen'
  | 'other'

export const STANDARD_LAB_LABELS: Record<StandardLabType, string> = {
  'blood-type-abo-rh': 'Blood Type & Rh',
  cbc: 'CBC',
  rubella: 'Rubella IgG',
  'hep-b-sag': 'Hepatitis B sAg',
  hiv: 'HIV',
  'syphilis-rpr': 'Syphilis / RPR',
  'gc-chlamydia': 'GC / Chlamydia',
  'urine-culture': 'Urine Culture',
  pap: 'Pap Smear',
  gct: 'Glucose Challenge (GCT)',
  gtt: 'Glucose Tolerance (GTT)',
  gbs: 'Group B Strep (GBS)',
  ua: 'Urinalysis (UA)',
  tsh: 'TSH',
  'antibody-screen': 'Antibody Screen',
  other: 'Other',
}

export interface LabResult {
  assessmentId: string | null // null = patient-level result, not tied to an assessment
  patientId: string
  practiceId: string
  labType: StandardLabType
  labName: string // auto-filled from STANDARD_LAB_LABELS, editable
  collectedDate?: string // YYYY-MM-DD
  resultDate?: string // YYYY-MM-DD
  result: string // e.g. "Negative", "7.2 mg/dL"
  unit?: string
  referenceRange?: string
  flagStatus: LabResultFlag
  // §5.4: Neonatal vs. maternal scoping. Absent or 'maternal' = maternal record.
  recordType?: 'maternal' | 'neonatal'
  // Provider acknowledgment
  acknowledgedBy?: string // provider userId
  acknowledgedAt?: string // ISO timestamp
  providerNote?: string
  providerNoteUpdatedAt?: string
}

// Lab types that have dedicated structured result fields in logLabResultField.
// These hide the generic free-text result / unit / referenceRange fields.
export const STRUCTURED_LAB_TYPES: ReadonlyArray<StandardLabType> = [
  'cbc',
  'blood-type-abo-rh',
  'gtt',
  'ua',
  'gc-chlamydia',
  'syphilis-rpr',
  'hep-b-sag',
  'hiv',
  'gbs',
  'antibody-screen',
] as const

// ─── Re-usable option sets ────────────────────────────────────────────────────

const negPosOptions = [
  { id: 'negative', text: 'Negative' },
  { id: 'positive', text: 'Positive' },
]

const uaScaleOptions = [
  { id: 'negative', text: 'Negative' },
  { id: 'trace', text: 'Trace' },
  { id: '1+', text: '1+' },
  { id: '2+', text: '2+' },
  { id: '3+', text: '3+' },
  { id: '4+', text: '4+' },
]

const uaNitritesOptions = [
  { id: 'negative', text: 'Negative' },
  { id: 'positive', text: 'Positive' },
]

// ─── FieldMap ─────────────────────────────────────────────────────────────────

export const logLabResultField: FieldMap = {
  name: 'Log Lab Result',
  // Auto-fill labName from the standard label whenever labType is selected.
  // calculateOnConditional clears hidden structured-result fields when labType changes.
  calculateOnConditional: true,
  calculate: [
    {
      field: 'labType',
      updates: {
        labName: (labType: string) =>
          labType ? (STANDARD_LAB_LABELS[labType as StandardLabType] ?? '') : '',
      },
    },
  ],
  validate: (values?: FieldMapValue) => {
    const v = values as AnyObject | undefined
    const errors: AnyObject = {}
    if (!v?.labType) errors.labType = 'Required'
    if (!v?.labName?.trim()) errors.labName = 'Required'
    // Generic result is only required when no structured result FieldMap applies
    if (
      v?.labType &&
      !STRUCTURED_LAB_TYPES.includes(v.labType as StandardLabType) &&
      !v?.result?.trim()
    ) {
      errors.result = 'Required'
    }
    return Object.keys(errors).length ? errors : undefined
  },
  children: {
    // ── Common fields ────────────────────────────────────────────────────────
    labType: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'Lab Type',
      options: (Object.keys(STANDARD_LAB_LABELS) as StandardLabType[]).map(id => ({
        id,
        text: STANDARD_LAB_LABELS[id],
      })),
    } as DropdownField<StandardLabType>,

    labName: {
      type: FieldTypes.TEXT,
      placeholder: 'Lab Name',
    } as TextField,

    collectedDate: {
      type: FieldTypes.DATE,
      placeholder: 'Collected Date',
      optional: true,
      isoFormat: true,
    },

    resultDate: {
      type: FieldTypes.DATE,
      placeholder: 'Result Date',
      optional: true,
      isoFormat: true,
    },

    // ── Generic result fields (non-structured lab types) ──────────────────────
    result: {
      type: FieldTypes.TEXT,
      placeholder: 'Result',
      optional: true,
      condition: v => !STRUCTURED_LAB_TYPES.includes(v?.labType as StandardLabType),
    } as TextField,

    unit: {
      type: FieldTypes.TEXT,
      placeholder: 'Unit — optional',
      optional: true,
      condition: v => !STRUCTURED_LAB_TYPES.includes(v?.labType as StandardLabType),
    } as TextField,

    referenceRange: {
      type: FieldTypes.TEXT,
      placeholder: 'Reference Range — optional',
      optional: true,
      condition: v => !STRUCTURED_LAB_TYPES.includes(v?.labType as StandardLabType),
    } as TextField,

    flagStatus: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'Flag Status',
      options: [
        { id: 'normal', text: 'Normal' },
        { id: 'high', text: 'HIGH' },
        { id: 'low', text: 'LOW' },
        { id: 'abnormal', text: 'Abnormal' },
        { id: 'critical', text: 'CRITICAL' },
      ],
    } as DropdownField<LabResultFlag>,

    // ── CBC ───────────────────────────────────────────────────────────────────
    // §2.7.1: Standard ACNM lab panel. Pregnancy reference ranges:
    // Hgb ≥10 g/dL, WBC 5–13×10³/μL, Platelets ≥100K, Hct ≥30%.
    cbcResults: {
      name: 'CBC Results',
      optional: true,
      condition: v => v?.labType === 'cbc',
      children: {
        hgb: {
          type: FieldTypes.NUMBER,
          placeholder: 'Hemoglobin',
          hints: [{ text: 'normal ≥10' }],
          unit: 'g/dL',
          optional: true,
          min: 0,
          max: 20,
          step: 0.1,
          precision: 1,
        } as NumberField,
        wbc: {
          type: FieldTypes.NUMBER,
          placeholder: 'WBC',
          unit: '×10³/μL',
          optional: true,
          min: 0,
          max: 50,
          step: 0.1,
          precision: 1,
        } as NumberField,
        platelets: {
          type: FieldTypes.NUMBER,
          placeholder: 'Platelets',
          hints: [{ text: 'normal ≥100K' }],
          unit: '×10³/μL',
          optional: true,
          min: 0,
          max: 1000,
        } as NumberField,
        hct: {
          type: FieldTypes.NUMBER,
          placeholder: 'Hematocrit',
          hints: [{ text: 'normal ≥30' }],
          unit: '%',
          optional: true,
          min: 0,
          max: 60,
          step: 0.1,
          precision: 1,
        } as NumberField,
      },
    },

    // ── Blood Type & Rh ───────────────────────────────────────────────────────
    bloodTypeResults: {
      name: 'Blood Type',
      optional: true,
      condition: v => v?.labType === 'blood-type-abo-rh',
      children: {
        aboType: {
          type: FieldTypes.DROPDOWN,
          placeholder: 'ABO Blood Type',
          inputType: 'radio',
          optional: true,
          options: [
            { id: 'A', text: 'A' },
            { id: 'B', text: 'B' },
            { id: 'AB', text: 'AB' },
            { id: 'O', text: 'O' },
          ],
        },
        rhFactor: {
          type: FieldTypes.DROPDOWN,
          placeholder: 'Rh Factor',
          inputType: 'radio',
          optional: true,
          options: [
            { id: 'positive', text: 'Positive (+)' },
            { id: 'negative', text: 'Negative (−)' },
          ],
        },
      },
    },

    // ── GTT (3-hour Glucose Tolerance Test) ───────────────────────────────────
    // Carpenter-Coustan criteria: GDM if ≥2 values meet thresholds
    // (<95 fasting / <180 at 1h / <155 at 2h / <140 at 3h).
    gttResults: {
      name: 'GTT Results — Carpenter-Coustan (mg/dL)',
      optional: true,
      condition: v => v?.labType === 'gtt',
      children: {
        fasting: {
          type: FieldTypes.NUMBER,
          placeholder: 'Fasting',
          hints: [{ text: 'threshold <95' }],
          unit: 'mg/dL',
          optional: true,
          min: 0,
          max: 400,
        } as NumberField,
        oneHour: {
          type: FieldTypes.NUMBER,
          placeholder: '1-Hour',
          hints: [{ text: 'threshold <180' }],
          unit: 'mg/dL',
          optional: true,
          min: 0,
          max: 600,
        } as NumberField,
        twoHour: {
          type: FieldTypes.NUMBER,
          placeholder: '2-Hour',
          hints: [{ text: 'threshold <155' }],
          unit: 'mg/dL',
          optional: true,
          min: 0,
          max: 600,
        } as NumberField,
        threeHour: {
          type: FieldTypes.NUMBER,
          placeholder: '3-Hour',
          hints: [{ text: 'threshold <140' }],
          unit: 'mg/dL',
          optional: true,
          min: 0,
          max: 600,
        } as NumberField,
      },
    },

    // ── Urinalysis ────────────────────────────────────────────────────────────
    urinalysisResults: {
      name: 'Urinalysis',
      optional: true,
      condition: v => v?.labType === 'ua',
      children: {
        protein: {
          type: FieldTypes.DROPDOWN,
          placeholder: 'Protein',
          inputType: 'radio',
          optional: true,
          options: uaScaleOptions,
        },
        glucose: {
          type: FieldTypes.DROPDOWN,
          placeholder: 'Glucose',
          inputType: 'radio',
          optional: true,
          options: uaScaleOptions,
        },
        leukocytes: {
          type: FieldTypes.DROPDOWN,
          placeholder: 'Leukocyte Esterase',
          inputType: 'radio',
          optional: true,
          options: uaScaleOptions.slice(0, 5), // Neg/Trace/1+/2+/3+ (no 4+)
        },
        nitrites: {
          type: FieldTypes.DROPDOWN,
          placeholder: 'Nitrites',
          inputType: 'radio',
          optional: true,
          options: uaNitritesOptions,
        },
        blood: {
          type: FieldTypes.DROPDOWN,
          placeholder: 'Blood',
          inputType: 'radio',
          optional: true,
          options: uaScaleOptions,
        },
      },
    },

    // ── GC / Chlamydia ────────────────────────────────────────────────────────
    gcChlamydiaResults: {
      name: 'GC / Chlamydia NAAT',
      optional: true,
      condition: v => v?.labType === 'gc-chlamydia',
      children: {
        gc: {
          type: FieldTypes.DROPDOWN,
          placeholder: 'N. gonorrhoeae',
          inputType: 'radio',
          optional: true,
          options: negPosOptions,
        },
        chlamydia: {
          type: FieldTypes.DROPDOWN,
          placeholder: 'C. trachomatis',
          inputType: 'radio',
          optional: true,
          options: negPosOptions,
        },
      },
    },

    // ── Syphilis / RPR ────────────────────────────────────────────────────────
    // Titer (e.g. "1:8") shown only when reactive.
    syphilisResults: {
      name: 'Syphilis / RPR',
      optional: true,
      condition: v => v?.labType === 'syphilis-rpr',
      children: {
        status: {
          type: FieldTypes.DROPDOWN,
          placeholder: 'RPR Result',
          inputType: 'radio',
          optional: true,
          options: [
            { id: 'non-reactive', text: 'Non-Reactive' },
            { id: 'reactive', text: 'Reactive' },
          ],
        },
        titer: {
          type: FieldTypes.TEXT,
          placeholder: 'Titer',
          hints: [{ text: 'e.g. 1:8' }],
          optional: true,
          condition: v => v?.status === 'reactive',
        } as TextField,
      },
    },

    // ── Hepatitis B sAg ───────────────────────────────────────────────────────
    hepBResult: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'Hepatitis B sAg Result',
      inputType: 'radio',
      optional: true,
      condition: v => v?.labType === 'hep-b-sag',
      options: [
        { id: 'non-reactive', text: 'Non-Reactive' },
        { id: 'reactive', text: 'Reactive (screen)' },
        { id: 'positive', text: 'Positive (confirmed)' },
        { id: 'negative', text: 'Negative (confirmed)' },
      ],
    },

    // ── HIV ───────────────────────────────────────────────────────────────────
    hivResult: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'HIV Result',
      inputType: 'radio',
      optional: true,
      condition: v => v?.labType === 'hiv',
      options: [
        { id: 'negative', text: 'Negative' },
        { id: 'positive', text: 'Positive' },
        { id: 'declined', text: 'Declined / Opted Out' },
        { id: 'inconclusive', text: 'Inconclusive / Indeterminate' },
      ],
    },

    // ── Group B Strep ─────────────────────────────────────────────────────────
    gbsResult: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'GBS Culture Result',
      inputType: 'radio',
      optional: true,
      condition: v => v?.labType === 'gbs',
      options: [
        { id: 'negative', text: 'Negative' },
        { id: 'positive', text: 'Positive' },
        { id: 'unknown', text: 'Unknown / Not Performed' },
      ],
    },

    // ── Rh Antibody Screen ────────────────────────────────────────────────────
    antibodyScreenStatus: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'Antibody Screen Result',
      inputType: 'radio',
      optional: true,
      condition: v => v?.labType === 'antibody-screen',
      options: [
        { id: 'negative', text: 'Negative — No antibodies detected' },
        { id: 'antibody-detected', text: 'Antibody Detected' },
      ],
    },

    antibodyType: {
      type: FieldTypes.TEXT,
      placeholder: 'Antibody Identified',
      hints: [{ text: 'e.g. anti-D, anti-Kell' }],
      optional: true,
      condition: v =>
        v?.labType === 'antibody-screen' && v?.antibodyScreenStatus === 'antibody-detected',
    } as TextField,
  },
}

// ─── Serializer ───────────────────────────────────────────────────────────────

type SerializedLabResult = Pick<
  LabResult,
  | 'labType'
  | 'labName'
  | 'result'
  | 'flagStatus'
  | 'unit'
  | 'referenceRange'
  | 'collectedDate'
  | 'resultDate'
>

export const serializeLabResult = (values: AnyObject): SerializedLabResult => {
  const { labType, labName, flagStatus, collectedDate, resultDate } = values as {
    labType: StandardLabType
    labName: string
    flagStatus: LabResultFlag
    collectedDate?: string
    resultDate?: string
  }

  let result = (values.result as string | undefined)?.trim() ?? ''
  let unit: string | undefined = (values.unit as string | undefined)?.trim() || undefined
  let referenceRange: string | undefined =
    (values.referenceRange as string | undefined)?.trim() || undefined

  switch (labType) {
    case 'cbc': {
      const r = (values.cbcResults ?? {}) as Record<string, number | undefined>
      const parts: string[] = []
      if (r.hgb != null) parts.push(`Hgb: ${r.hgb} g/dL`)
      if (r.wbc != null) parts.push(`WBC: ${r.wbc} ×10³/μL`)
      if (r.platelets != null) parts.push(`Plts: ${r.platelets} ×10³/μL`)
      if (r.hct != null) parts.push(`Hct: ${r.hct}%`)
      result = parts.join(' | ')
      referenceRange = 'Hgb ≥10, Plts ≥100K'
      break
    }

    case 'blood-type-abo-rh': {
      const r = (values.bloodTypeResults ?? {}) as Record<string, string | undefined>
      const rh =
        r.rhFactor === 'positive' ? 'Positive' : r.rhFactor === 'negative' ? 'Negative' : r.rhFactor
      result = [r.aboType, rh].filter(Boolean).join(' ')
      break
    }

    case 'gtt': {
      const r = (values.gttResults ?? {}) as Record<string, number | undefined>
      const parts: string[] = []
      if (r.fasting != null) parts.push(`Fasting: ${r.fasting}`)
      if (r.oneHour != null) parts.push(`1h: ${r.oneHour}`)
      if (r.twoHour != null) parts.push(`2h: ${r.twoHour}`)
      if (r.threeHour != null) parts.push(`3h: ${r.threeHour}`)
      result = parts.join(' | ')
      unit = 'mg/dL'
      referenceRange = '<95 / <180 / <155 / <140 (Carpenter-Coustan)'
      break
    }

    case 'ua': {
      const r = (values.urinalysisResults ?? {}) as Record<string, string | undefined>
      const parts: string[] = []
      if (r.protein) parts.push(`Protein: ${r.protein}`)
      if (r.glucose) parts.push(`Glucose: ${r.glucose}`)
      if (r.leukocytes) parts.push(`Leukocytes: ${r.leukocytes}`)
      if (r.nitrites) parts.push(`Nitrites: ${r.nitrites}`)
      if (r.blood) parts.push(`Blood: ${r.blood}`)
      result = parts.join(' | ')
      break
    }

    case 'gc-chlamydia': {
      const r = (values.gcChlamydiaResults ?? {}) as Record<string, string | undefined>
      const parts: string[] = []
      if (r.gc) parts.push(`GC: ${r.gc}`)
      if (r.chlamydia) parts.push(`Chlamydia: ${r.chlamydia}`)
      result = parts.join(' | ')
      break
    }

    case 'syphilis-rpr': {
      const r = (values.syphilisResults ?? {}) as Record<string, string | undefined>
      result = [r.status, r.titer ? `Titer ${r.titer}` : null].filter(Boolean).join(' — ')
      break
    }

    case 'hep-b-sag':
      result = (values.hepBResult as string | undefined) ?? ''
      break

    case 'hiv':
      result = (values.hivResult as string | undefined) ?? ''
      break

    case 'gbs':
      result = (values.gbsResult as string | undefined) ?? ''
      break

    case 'antibody-screen': {
      const status = (values.antibodyScreenStatus as string | undefined) ?? ''
      const type = (values.antibodyType as string | undefined) ?? ''
      result = [status, type ? `(${type})` : null].filter(Boolean).join(' ')
      break
    }
  }

  return {
    labType,
    labName: labName?.trim() ?? '',
    result,
    flagStatus: flagStatus ?? 'normal',
    ...(collectedDate ? { collectedDate } : {}),
    ...(resultDate ? { resultDate } : {}),
    ...(unit ? { unit } : {}),
    ...(referenceRange ? { referenceRange } : {}),
  }
}
